import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { Auth, GoogleAuthProvider, OAuthProvider, signInWithPopup, updateProfile } from '@angular/fire/auth';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../auth.service';
import { LanguageSelectorComponent } from '../../shared/components/language-selector/language-selector.component';
import { TenantBrandingService } from '../../shared/services/tenant-branding/tenant-branding.service';
import { ToastService } from '../../shared/services/toast/toast.service';

@Component({
  selector: 'app-signin',
  templateUrl: './signin.component.html',
  styleUrls: ['./signin.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressBarModule,
    RouterLink,
    LanguageSelectorComponent,
  ],
})
export class SigninComponent implements OnInit {
  public tenantBrandingService = inject(TenantBrandingService);
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private router = inject(Router);
  private translate = inject(TranslateService);
  private aroute = inject(ActivatedRoute);
  private toast = inject(ToastService);

  onLogoError(event: Event): void {
    (event.target as HTMLImageElement).src = 'assets/creatives/logo-w70.png';
  }
  private auth = inject(Auth);

  loading = signal(false);
  hidePassword = signal(true);
  isNewUser = signal(false);
  inviteInfo = signal<any>(null);
  inviteAccepted = signal(false);
  inviteError = signal(false);
  hideConfirmPassword = signal(true);
  alreadyLoggedIn = signal(false);
  currentUserEmail = signal('');

  get passwordMismatch(): boolean {
    if (!this.signinForm || !this.isNewUser()) return false;
    const pw = this.signinForm.get('password')?.value;
    const cpw = this.signinForm.get('confirmPassword')?.value;
    return cpw?.length > 0 && pw !== cpw;
  }

  signinForm: FormGroup;
  inviteToken: string | null = null;

  ngOnInit() {
    const lang = this.translate.currentLang || localStorage.getItem('language') || 'fr';
    this.translate.use(lang);

    this.auth.onAuthStateChanged((user) => {
      if (user) {
        this.alreadyLoggedIn.set(true);
        this.currentUserEmail.set(user.email || '');
      } else {
        this.alreadyLoggedIn.set(false);
        this.currentUserEmail.set('');
      }
    });

    this.signinForm = this.fb.group({
      email: [{ value: '', disabled: true }, [Validators.required, Validators.email]],
      fullName: ['', [Validators.required]],
      password: ['', [Validators.required]],
      confirmPassword: [''],
    });

    const url = new URL(location.href);
    this.inviteToken = url.searchParams.get('invite') || this.aroute.snapshot.queryParamMap.get('invite');

    if (this.inviteToken) {
      this.loading.set(true);
      this.authService.getInviteInfo(this.inviteToken).subscribe({
        next: (res) => {
          this.loading.set(false);
          if (res.payload) {
            this.inviteInfo.set(res.payload);
            this.isNewUser.set(!res.payload.userExists);
            this.signinForm.patchValue({ email: res.payload.email });
          }
        },
        error: () => {
          this.loading.set(false);
          this.inviteError.set(true);
        },
      });
    } else {
      this.router.navigate(['/login'], { queryParamsHandling: 'preserve' });
    }
  }

  toggleHide() {
    this.hidePassword.update((h) => !h);
  }

  /** Step 1: User explicitly accepts the invitation → show auth form */
  acceptAndContinue() {
    this.inviteAccepted.set(true);
  }

  /** User declines → redirect to main login */
  declineInvite() {
    if (this.alreadyLoggedIn()) {
      this.auth.signOut().then(() => {
        this.router.navigate(['/login']);
      });
    } else {
      this.router.navigate(['/login']);
    }
  }

  async acceptInviteDirectly() {
    this.loading.set(true);
    const tenantId = await this.handleInviteAcceptance();
    if (tenantId) {
      localStorage.setItem('active_workspace', tenantId);
      this.router.navigateByUrl('/knowledge');
    }
    this.loading.set(false);
  }

  async signinWithProvider(providerType: 'google' | 'microsoft') {
    if (!this.inviteToken) return;

    this.loading.set(true);
    try {
      // Sign in via Firebase popup directly — do NOT call loginWithProvider
      // because it tries to fetch userData from MongoDB before acceptInvite
      // has created the user document (chicken-and-egg problem).
      const provider = providerType === 'google'
        ? new GoogleAuthProvider()
        : new OAuthProvider('microsoft.com');

      const result = await signInWithPopup(this.auth, provider);
      const token = await result.user.getIdToken();
      localStorage.setItem('loginData', JSON.stringify({ token }));

      // Now accept the invite — this creates the user doc in MongoDB if needed
      const tenantId = await this.handleInviteAcceptance();
      if (tenantId) {
        localStorage.setItem('active_workspace', tenantId);
      }

      // Finally fetch user data now that the MongoDB doc exists
      await this.authService.getUserData({ id: result.user.uid });
      this.loading.set(false);
      this.router.navigateByUrl('/knowledge');
    } catch (err) {
      this.loading.set(false);
      console.error(err);
      this.toast.snackbar.open(
        this.translate.instant('Snackbar.ErrorOccured') || 'An error occurred',
        'Ok',
        { duration: 3000 }
      );
    }
  }

  async onSubmit() {
    if (this.signinForm.invalid && !this.isNewUser()) {
      return;
    }
    if (this.isNewUser() && this.signinForm.get('password')?.invalid) {
      return;
    }
    if (this.isNewUser() && this.signinForm.getRawValue().password !== this.signinForm.getRawValue().confirmPassword) {
      this.toast.snackbar.open(
        this.translate.instant('Sessions.Login.PasswordMismatch'),
        'Ok',
        { duration: 3000 }
      );
      return;
    }

    this.loading.set(true);
    const email = this.signinForm.getRawValue().email;
    const password = this.signinForm.getRawValue().password;

    try {
      if (this.isNewUser()) {
        const cred = await this.authService.signupWithEmailAndPassword(email, password);
        const fullName = this.signinForm.getRawValue().fullName?.trim();
        if (fullName && cred.user) {
          await updateProfile(cred.user, { displayName: fullName });
        }
      } else {
        await this.authService.login({ email, password });
      }
      const tenantId = await this.handleInviteAcceptance();
      if (tenantId) {
        localStorage.setItem('active_workspace', tenantId);
      }
      this.loading.set(false);
      this.router.navigateByUrl('/knowledge');
    } catch (error: any) {
      this.loading.set(false);
      console.error(error);

      let msgKey = 'Snackbar.CredentialsErrors';
      const code = error?.code || '';

      if (code === 'auth/weak-password' || code === 'auth/password-does-not-meet-requirements') {
        msgKey = 'Sessions.Signin.WeakPasswordError';
      } else if (code === 'auth/email-already-in-use') {
        msgKey = 'Sessions.Signin.EmailAlreadyInUseError';
      } else if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        msgKey = 'Snackbar.CredentialsErrors';
      }

      this.toast.snackbar.open(
        this.translate.instant(msgKey),
        'Ok',
        { duration: 5000 }
      );
    }
  }

  /**
   * Sends the invite acceptance to the backend.
   * Returns the tenantId on success so the caller can set active_workspace.
   */
  private async handleInviteAcceptance(): Promise<string | null> {
    if (!this.inviteToken) return null;

    try {
      const res = await firstValueFrom(this.authService.acceptInvite(this.inviteToken));
      if (res.success) {
        this.toast.snackbar.open(
          this.translate.instant('Sessions.Signin.InvitationAcceptedToast') || 'Invitation accepted!',
          'Ok',
          { duration: 2000 }
        );
        return res.tenantId || null;
      }
      return null;
    } catch (err) {
      console.error('Failed to accept invite', err);
      this.toast.snackbar.open(
        this.translate.instant('Snackbar.Error') || 'Error accepting invitation',
        'Ok',
        { duration: 3000 }
      );
      return null;
    }
  }
}
